import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  TextField,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  People as ReferralIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AttachMoney as RevenueIcon,
  CheckCircle as ConvertedIcon,
  PendingActions as PendingIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Share as ShareIcon,
  QrCode as QRCodeIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const ReferralManagement = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  // Mock referral data
  const referrals = [
    {
      id: 1,
      referrerName: "Greenwood High School",
      referrerContact: "John Doe",
      referrerEmail: "john@greenwood.edu",
      referredSchool: "Bright Future School",
      referredContact: "Rajesh Kumar",
      referredEmail: "rajesh@brightfuture.edu",
      referredPhone: "+91 98765 43210",
      status: "Converted",
      referralDate: "2024-01-10",
      conversionDate: "2024-01-15",
      commission: 5000,
      commissionStatus: "Paid",
      referralCode: "REF-GHS-001",
      reward: "₹5,000 + 1 Month Free",
    },
    {
      id: 2,
      referrerName: "Sunshine Academy",
      referrerContact: "Jane Smith",
      referrerEmail: "jane@sunshine.edu",
      referredSchool: "Excel Academy",
      referredContact: "Priya Sharma",
      referredEmail: "priya@excel.edu",
      referredPhone: "+91 98765 43211",
      status: "Pending",
      referralDate: "2024-01-12",
      conversionDate: null,
      commission: 0,
      commissionStatus: "Pending",
      referralCode: "REF-SUN-002",
      reward: "₹5,000 + 1 Month Free",
    },
    {
      id: 3,
      referrerName: "Riverside School",
      referrerContact: "Mike Johnson",
      referrerEmail: "mike@riverside.edu",
      referredSchool: "Knowledge Hub",
      referredContact: "Amit Patel",
      referredEmail: "amit@knowledgehub.edu",
      referredPhone: "+91 98765 43212",
      status: "Qualified",
      referralDate: "2024-01-08",
      conversionDate: null,
      commission: 0,
      commissionStatus: "Pending",
      referralCode: "REF-RIV-003",
      reward: "₹5,000 + 1 Month Free",
    },
  ];

  const referrers = [
    {
      id: 1,
      name: "Greenwood High School",
      contact: "John Doe",
      email: "john@greenwood.edu",
      totalReferrals: 5,
      convertedReferrals: 3,
      totalCommission: 15000,
      pendingCommission: 0,
      tier: "Gold",
      referralCode: "REF-GHS-001",
    },
    {
      id: 2,
      name: "Sunshine Academy",
      contact: "Jane Smith",
      email: "jane@sunshine.edu",
      totalReferrals: 3,
      convertedReferrals: 1,
      totalCommission: 5000,
      pendingCommission: 10000,
      tier: "Silver",
      referralCode: "REF-SUN-002",
    },
    {
      id: 3,
      name: "Riverside School",
      contact: "Mike Johnson",
      email: "mike@riverside.edu",
      totalReferrals: 2,
      convertedReferrals: 0,
      totalCommission: 0,
      pendingCommission: 10000,
      tier: "Bronze",
      referralCode: "REF-RIV-003",
    },
  ];

  const referralProgram = {
    commissionPerConversion: 5000,
    bonusMonths: 1,
    tiers: [
      { name: "Bronze", minReferrals: 1, discount: "5%", bonus: "1 Month Free" },
      { name: "Silver", minReferrals: 3, discount: "10%", bonus: "2 Months Free" },
      { name: "Gold", minReferrals: 5, discount: "15%", bonus: "3 Months Free" },
    ],
    totalReferrals: referrals.length,
    convertedReferrals: referrals.filter((r) => r.status === "Converted").length,
    totalCommissionPaid: referrals.filter((r) => r.commissionStatus === "Paid").reduce((sum, r) => sum + r.commission, 0),
    pendingCommission: referrals.filter((r) => r.commissionStatus === "Pending").reduce((sum, r) => sum + r.commission, 0),
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Converted":
        return "success";
      case "Qualified":
        return "info";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  const handleCopyReferralCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Referral code copied!");
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
              <ReferralIcon sx={{ color: theme.palette.primary.main }} />
              Referral Management
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Manage referral program, track referrals, and reward referrers
            </Typography>
          </Box>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setReferralDialogOpen(true)}>
            Add Referral
          </Button>
        </Box>

        {/* Program Stats */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Referrals
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {referralProgram.totalReferrals}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {referralProgram.convertedReferrals} Converted
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Conversion Rate
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {Math.round((referralProgram.convertedReferrals / referralProgram.totalReferrals) * 100) || 0}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(referralProgram.convertedReferrals / referralProgram.totalReferrals) * 100 || 0}
                  sx={{ mt: 1, height: 4, borderRadius: 2 }}
                  color="success"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Commission Paid
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                  ₹{referralProgram.totalCommissionPaid.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  ₹{referralProgram.pendingCommission.toLocaleString()} Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Active Referrers
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {referrers.length}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {referrers.filter((r) => r.tier === "Gold").length} Gold Tier
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Referrals" />
            <Tab label="Referrers" />
            <Tab label="Program Settings" />
          </Tabs>
        </Card>

        {/* Referrals Tab */}
        {selectedTab === 0 && (
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search referrals..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                  sx={{ flexGrow: 1, minWidth: 250 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" defaultValue="all">
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="Converted">Converted</MenuItem>
                    <MenuItem value="Qualified">Qualified</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Referrer</TableCell>
                      <TableCell>Referred School</TableCell>
                      <TableCell>Referral Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Commission</TableCell>
                      <TableCell>Referral Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referrals.map((referral) => (
                      <TableRow key={referral.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                              {referral.referrerName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              {referral.referrerContact}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                              {referral.referredSchool}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              {referral.referredContact} • {referral.referredPhone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem", fontFamily: "monospace" }}>
                              {referral.referralCode}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyReferralCode(referral.referralCode)}
                              sx={{ p: 0.5 }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={referral.status}
                            size="small"
                            color={getStatusColor(referral.status)}
                            sx={{ fontSize: "0.7rem", height: "22px" }}
                          />
                        </TableCell>
                        <TableCell>
                          {referral.commission > 0 ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 600, color: theme.palette.success.main }}>
                                ₹{referral.commission.toLocaleString()}
                              </Typography>
                              <Chip
                                label={referral.commissionStatus}
                                size="small"
                                color={referral.commissionStatus === "Paid" ? "success" : "warning"}
                                sx={{ fontSize: "0.65rem", height: "18px", mt: 0.25 }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                              Pending
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            {referral.referralDate}
                          </Typography>
                          {referral.conversionDate && (
                            <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "success.main", display: "block" }}>
                              Converted: {referral.conversionDate}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Referrers Tab */}
        {selectedTab === 1 && (
          <Grid container spacing={2}>
            {referrers.map((referrer) => (
              <Grid item xs={12} md={6} key={referrer.id}>
                <Card
                  sx={{
                    border: `2px solid ${
                      referrer.tier === "Gold"
                        ? "#FFD700"
                        : referrer.tier === "Silver"
                        ? "#C0C0C0"
                        : "#CD7F32"
                    }`,
                    "&:hover": {
                      boxShadow: 4,
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                          <SchoolIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                            {referrer.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                            {referrer.contact}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={referrer.tier}
                        size="small"
                        sx={{
                          bgcolor:
                            referrer.tier === "Gold"
                              ? "#FFD700"
                              : referrer.tier === "Silver"
                              ? "#C0C0C0"
                              : "#CD7F32",
                          color: "white",
                          fontSize: "0.7rem",
                          height: "22px",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                            Total Referrals
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.primary.main }}>
                            {referrer.totalReferrals}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                            Converted
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.success.main }}>
                            {referrer.convertedReferrals}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mb: 2, p: 1, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                          Total Commission
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: theme.palette.success.main }}>
                          ₹{referrer.totalCommission.toLocaleString()}
                        </Typography>
                      </Box>
                      {referrer.pendingCommission > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Pending
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: theme.palette.warning.main }}>
                            ₹{referrer.pendingCommission.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block" }}>
                            Referral Code
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.875rem", fontWeight: 600 }}>
                            {referrer.referralCode}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Copy Code">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyReferralCode(referrer.referralCode)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate QR Code">
                            <IconButton size="small">
                              <QRCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Share Link">
                            <IconButton size="small">
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                    <Button variant="outlined" size="small" fullWidth startIcon={<ViewIcon />}>
                      View All Referrals
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Program Settings Tab */}
        {selectedTab === 2 && (
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                Referral Program Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: "0.95rem" }}>
                        Commission Structure
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          label="Commission per Conversion"
                          type="number"
                          defaultValue={referralProgram.commissionPerConversion}
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="Bonus Months"
                          type="number"
                          defaultValue={referralProgram.bonusMonths}
                          size="small"
                          helperText="Free months for referrer"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: "0.95rem" }}>
                        Tier Benefits
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {referralProgram.tiers.map((tier, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                                {tier.name} Tier
                              </Typography>
                              <Chip
                                label={`${tier.minReferrals}+ Referrals`}
                                size="small"
                                sx={{ fontSize: "0.7rem", height: "20px" }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                              Discount: {tier.discount} • Bonus: {tier.bonus}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Add Referral Dialog */}
        <Dialog open={referralDialogOpen} onClose={() => setReferralDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Referral</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Referrer School</InputLabel>
                  <Select label="Referrer School">
                    {referrers.map((r) => (
                      <MenuItem key={r.id} value={r.name}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Referred School Name" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Contact Person" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number" size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Email" type="email" size="small" />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" defaultValue="Pending">
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Qualified">Qualified</MenuItem>
                    <MenuItem value="Converted">Converted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReferralDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => { setReferralDialogOpen(false); toast.success("Referral added successfully!"); }}>
              Add Referral
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default ReferralManagement;

